import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiSearch,
  FiPlus,
  FiX,
  FiCheck,
  FiUsers,
  FiUserMinus,
  FiEdit2,
  FiAlertCircle,
  FiLoader,
  FiExternalLink,
  FiShield,
  FiUpload,
  FiBell,
  FiCalendar,
} from "react-icons/fi";
import { workshopApi } from "../../services/workshopApi";
import "./CoOrganizerManagement.css";

// ========================================
// ANIMATION VARIANTS
// ========================================
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3, ease: "easeOut" },
  },
};

const modalVariants = {
  hidden: { opacity: 0, scale: 0.95 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.2, ease: "easeOut" },
  },
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: { duration: 0.15 },
  },
};

// ========================================
// CONSTANTS
// ========================================
const MAX_CO_ORGANIZERS = 5;

const DEFAULT_PERMISSIONS = {
  canApproveParticipants: true,
  canUploadResources: true,
  canSendAnnouncements: false,
  canManageSchedule: false,
};

const PERMISSION_CONFIG = [
  {
    key: "canApproveParticipants",
    label: "Approve Participants",
    description: "Can approve or reject workshop applications",
    icon: FiUsers,
    defaultValue: true,
  },
  {
    key: "canUploadResources",
    label: "Upload Resources",
    description: "Can upload slides, documents, and materials",
    icon: FiUpload,
    defaultValue: true,
  },
  {
    key: "canSendAnnouncements",
    label: "Send Announcements",
    description: "Can send announcements to all participants",
    icon: FiBell,
    defaultValue: false,
  },
  {
    key: "canManageSchedule",
    label: "Manage Schedule",
    description: "Can edit workshop schedule and sessions",
    icon: FiCalendar,
    defaultValue: false,
  },
];

// ========================================
// SUB-COMPONENTS
// ========================================

// Search Result User Card
const SearchResultCard = ({ user, onSelect, isSelecting }) => (
  <motion.div
    className="com-search-result"
    variants={itemVariants}
    onClick={() => !isSelecting && onSelect(user)}
    whileHover={{ backgroundColor: "var(--bg-hover)" }}
  >
    <div className="com-search-result-avatar">
      {user.profilePhotoUrl ? (
        <img src={user.profilePhotoUrl} alt={user.name} />
      ) : (
        <span>{user.name?.charAt(0)?.toUpperCase() || "?"}</span>
      )}
    </div>
    <div className="com-search-result-info">
      <span className="com-search-result-name">{user.name}</span>
      <span className="com-search-result-email">{user.email}</span>
      {user.professionalRole && (
        <span className="com-search-result-role">{user.professionalRole}</span>
      )}
    </div>
    {isSelecting ? (
      <FiLoader className="com-spinner" />
    ) : (
      <FiPlus className="com-add-icon" />
    )}
  </motion.div>
);

// Permission Toggle Component
const PermissionToggle = ({ config, checked, onChange, disabled }) => {
  const Icon = config.icon;
  return (
    <label className={`com-permission-toggle ${disabled ? "com-disabled" : ""}`}>
      <div className="com-permission-toggle-header">
        <input
          type="checkbox"
          checked={checked}
          onChange={(e) => onChange(config.key, e.target.checked)}
          disabled={disabled}
        />
        <Icon className="com-permission-icon" />
        <span className="com-permission-label">{config.label}</span>
      </div>
      <span className="com-permission-desc">{config.description}</span>
    </label>
  );
};

// Permission Badge (View Mode)
const PermissionBadge = ({ active, children }) => (
  <span className={`com-permission-badge ${active ? "com-active" : "com-inactive"}`}>
    {active ? <FiCheck size={12} /> : <FiX size={12} />}
    {children}
  </span>
);

// Co-Organizer Card Component
const CoOrganizerCard = ({
  coOrganizer,
  isOrganizer,
  onUpdatePermissions,
  onRemove,
  isUpdating,
  isRemoving,
}) => {
  const navigate = useNavigate();
  const [isEditing, setIsEditing] = useState(false);
  const [editPermissions, setEditPermissions] = useState(
    coOrganizer.permissions || DEFAULT_PERMISSIONS
  );
  const [showConfirmRemove, setShowConfirmRemove] = useState(false);

  const handleSavePermissions = async () => {
    const success = await onUpdatePermissions(coOrganizer.user._id, editPermissions);
    if (success) {
      setIsEditing(false);
    }
  };

  const handleRemove = async () => {
    await onRemove(coOrganizer.user._id, coOrganizer.user.name);
    setShowConfirmRemove(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <motion.div className="com-card" variants={itemVariants}>
      {/* User Info Section */}
      <div className="com-card-header">
        <div
          className="com-card-user"
          onClick={() => navigate(`/profile/${coOrganizer.user._id}`)}
        >
          <div className="com-card-avatar">
            {coOrganizer.user.profilePhotoUrl ? (
              <img src={coOrganizer.user.profilePhotoUrl} alt={coOrganizer.user.name} />
            ) : (
              <span>{coOrganizer.user.name?.charAt(0)?.toUpperCase() || "?"}</span>
            )}
          </div>
          <div className="com-card-info">
            <span className="com-card-name">
              {coOrganizer.user.name}
              <FiExternalLink size={12} className="com-profile-link" />
            </span>
            {coOrganizer.user.professionalRole && (
              <span className="com-card-role">{coOrganizer.user.professionalRole}</span>
            )}
            <span className="com-card-added">Added {formatDate(coOrganizer.addedAt)}</span>
          </div>
        </div>

        {/* Actions (Organizer Only) */}
        {isOrganizer && (
          <div className="com-card-actions">
            {!isEditing && (
              <button
                className="com-btn com-btn-ghost"
                onClick={() => {
                  setEditPermissions(coOrganizer.permissions || DEFAULT_PERMISSIONS);
                  setIsEditing(true);
                }}
                disabled={isUpdating || isRemoving}
              >
                <FiEdit2 size={14} />
                Edit
              </button>
            )}
            <button
              className="com-btn com-btn-danger"
              onClick={() => setShowConfirmRemove(true)}
              disabled={isUpdating || isRemoving}
            >
              {isRemoving ? <FiLoader className="com-spinner" /> : <FiUserMinus size={14} />}
              Remove
            </button>
          </div>
        )}
      </div>

      {/* Permissions Section */}
      {isOrganizer && coOrganizer.permissions && (
        <div className="com-card-permissions">
          {isEditing ? (
            // Edit Mode
            <div className="com-edit-permissions">
              <h5>Edit Permissions</h5>
              <div className="com-permissions-grid">
                {PERMISSION_CONFIG.map((config) => (
                  <PermissionToggle
                    key={config.key}
                    config={config}
                    checked={editPermissions[config.key]}
                    onChange={(key, value) =>
                      setEditPermissions((prev) => ({ ...prev, [key]: value }))
                    }
                    disabled={isUpdating}
                  />
                ))}
              </div>
              <div className="com-edit-actions">
                <button
                  className="com-btn com-btn-primary"
                  onClick={handleSavePermissions}
                  disabled={isUpdating}
                >
                  {isUpdating ? <FiLoader className="com-spinner" /> : <FiCheck size={14} />}
                  Save Changes
                </button>
                <button
                  className="com-btn com-btn-ghost"
                  onClick={() => setIsEditing(false)}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            // View Mode
            <div className="com-view-permissions">
              <div className="com-permissions-badges">
                <PermissionBadge active={coOrganizer.permissions.canApproveParticipants}>
                  Participants
                </PermissionBadge>
                <PermissionBadge active={coOrganizer.permissions.canUploadResources}>
                  Resources
                </PermissionBadge>
                <PermissionBadge active={coOrganizer.permissions.canSendAnnouncements}>
                  Announcements
                </PermissionBadge>
                <PermissionBadge active={coOrganizer.permissions.canManageSchedule}>
                  Schedule
                </PermissionBadge>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Remove Confirmation Modal */}
      <AnimatePresence>
        {showConfirmRemove && (
          <motion.div
            className="com-modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowConfirmRemove(false)}
          >
            <motion.div
              className="com-modal"
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="com-modal-icon com-modal-icon-warning">
                <FiAlertCircle />
              </div>
              <h3>Remove Co-Organizer?</h3>
              <p>
                Are you sure you want to remove <strong>{coOrganizer.user.name}</strong> as a
                co-organizer? They will lose all management access to this workshop.
              </p>
              <div className="com-modal-actions">
                <button
                  className="com-btn com-btn-danger"
                  onClick={handleRemove}
                  disabled={isRemoving}
                >
                  {isRemoving ? <FiLoader className="com-spinner" /> : "Yes, Remove"}
                </button>
                <button
                  className="com-btn com-btn-ghost"
                  onClick={() => setShowConfirmRemove(false)}
                  disabled={isRemoving}
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Add Co-Organizer Panel
const AddCoOrganizerPanel = ({
  workshopId,
  onCoOrganizerAdded,
  currentCount,
  onClose,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState(null);

  const handleSearch = useCallback(async () => {
    setIsSearching(true);
    setError(null);
    try {
      const response = await workshopApi.searchUsersForCoOrganizer(workshopId, searchQuery);
      if (response.success) {
        setSearchResults(response.data.users || []);
      }
    } catch (err) {
      setError("Failed to search users");
} finally {
      setIsSearching(false);
    }
  }, [workshopId, searchQuery]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, handleSearch]);

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setSearchQuery("");
    setSearchResults([]);
    setPermissions(DEFAULT_PERMISSIONS);
  };

  const handleAddCoOrganizer = async () => {
    if (!selectedUser) return;

    setIsAdding(true);
    setError(null);
    try {
      const response = await workshopApi.addCoOrganizer(workshopId, {
        userId: selectedUser._id,
        permissions,
      });

      if (response.success) {
        onCoOrganizerAdded(response.data);
        setSelectedUser(null);
        setPermissions(DEFAULT_PERMISSIONS);
      }
    } catch (err) {
      setError(err.message || "Failed to add co-organizer");
} finally {
      setIsAdding(false);
    }
  };

  const isFull = currentCount >= MAX_CO_ORGANIZERS;

  return (
    <motion.div
      className="com-add-panel"
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
    >
      <div className="com-add-panel-header">
        <h4>
          <FiPlus /> Add Co-Organizer
        </h4>
        <button className="com-close-btn" onClick={onClose}>
          <FiX />
        </button>
      </div>

      {isFull ? (
        <div className="com-limit-warning">
          <FiAlertCircle />
          <span>Maximum co-organizers ({MAX_CO_ORGANIZERS}) reached. Remove one to add another.</span>
        </div>
      ) : (
        <>
          {/* Search Input */}
          {!selectedUser && (
            <div className="com-search-section">
              <div className="com-search-input-wrapper">
                <FiSearch className="com-search-icon" />
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="com-search-input"
                  autoFocus
                />
                {isSearching && <FiLoader className="com-spinner com-search-spinner" />}
              </div>

              {/* Search Results */}
              {searchResults.length > 0 && (
                <motion.div
                  className="com-search-results"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  {searchResults.map((user) => (
                    <SearchResultCard
                      key={user._id}
                      user={user}
                      onSelect={handleSelectUser}
                      isSelecting={false}
                    />
                  ))}
                </motion.div>
              )}

              {searchQuery.length >= 2 && !isSearching && searchResults.length === 0 && (
                <div className="com-no-results">
                  <span>No users found matching "{searchQuery}"</span>
                </div>
              )}

              {searchQuery.length > 0 && searchQuery.length < 2 && (
                <div className="com-search-hint">
                  <span>Type at least 2 characters to search</span>
                </div>
              )}
            </div>
          )}

          {/* Selected User + Permissions */}
          {selectedUser && (
            <div className="com-selected-user-panel">
              <div className="com-selected-user">
                <div className="com-selected-avatar">
                  {selectedUser.profilePhotoUrl ? (
                    <img src={selectedUser.profilePhotoUrl} alt={selectedUser.name} />
                  ) : (
                    <span>{selectedUser.name?.charAt(0)?.toUpperCase() || "?"}</span>
                  )}
                </div>
                <div className="com-selected-info">
                  <span className="com-selected-name">{selectedUser.name}</span>
                  <span className="com-selected-email">{selectedUser.email}</span>
                  {selectedUser.professionalRole && (
                    <span className="com-selected-role">{selectedUser.professionalRole}</span>
                  )}
                </div>
                <button
                  className="com-deselect-btn"
                  onClick={() => setSelectedUser(null)}
                  disabled={isAdding}
                >
                  <FiX />
                </button>
              </div>

              <div className="com-permissions-section">
                <h5>
                  <FiShield /> Set Permissions
                </h5>
                <div className="com-permissions-grid">
                  {PERMISSION_CONFIG.map((config) => (
                    <PermissionToggle
                      key={config.key}
                      config={config}
                      checked={permissions[config.key]}
                      onChange={(key, value) =>
                        setPermissions((prev) => ({ ...prev, [key]: value }))
                      }
                      disabled={isAdding}
                    />
                  ))}
                </div>
              </div>

              {error && (
                <div className="com-error">
                  <FiAlertCircle />
                  <span>{error}</span>
                </div>
              )}

              <button
                className="com-btn com-btn-primary com-btn-full"
                onClick={handleAddCoOrganizer}
                disabled={isAdding}
              >
                {isAdding ? (
                  <>
                    <FiLoader className="com-spinner" />
                    Adding...
                  </>
                ) : (
                  <>
                    <FiPlus />
                    Add as Co-Organizer
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}
    </motion.div>
  );
};

// Empty State
const EmptyState = ({ isOrganizer, onAddClick }) => (
  <motion.div className="com-empty-state" variants={itemVariants}>
    <div className="com-empty-icon">
      <FiUsers />
    </div>
    <h4>No Co-Organizers Yet</h4>
    <p>
      {isOrganizer
        ? "Add co-organizers to help manage your workshop. They can approve participants, upload resources, and more."
        : "This workshop doesn't have any co-organizers."}
    </p>
    {isOrganizer && (
      <button className="com-btn com-btn-primary" onClick={onAddClick}>
        <FiPlus />
        Add Co-Organizer
      </button>
    )}
  </motion.div>
);

// ========================================
// MAIN COMPONENT
// ========================================
const CoOrganizerManagement = ({
  workshopId,
  isOrganizer = false,
  initialCoOrganizers = [],
  onUpdate,
}) => {
  const [coOrganizers, setCoOrganizers] = useState(initialCoOrganizers);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showAddPanel, setShowAddPanel] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  const [removingId, setRemovingId] = useState(null);

  const fetchCoOrganizers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await workshopApi.getCoOrganizers(workshopId);
      if (response.success) {
        setCoOrganizers(response.data.coOrganizers || []);
      }
    } catch (err) {
      setError("Failed to load co-organizers");
} finally {
      setLoading(false);
    }
  }, [workshopId]);

  // Fetch co-organizers on mount if not provided
  useEffect(() => {
    if (initialCoOrganizers.length === 0 && workshopId) {
      fetchCoOrganizers();
    }
  }, [workshopId, initialCoOrganizers.length, fetchCoOrganizers]);

  const handleCoOrganizerAdded = useCallback((data) => {
    // Build the new co-organizer object from response
    const newCoOrganizer = {
      user: data.user,
      addedAt: data.coOrganizer.addedAt,
      addedBy: data.coOrganizer.addedBy,
      permissions: data.coOrganizer.permissions,
    };

    setCoOrganizers((prev) => [...prev, newCoOrganizer]);
    setShowAddPanel(false);

    if (onUpdate) {
      onUpdate();
    }
  }, [onUpdate]);

  const handleUpdatePermissions = useCallback(async (userId, permissions) => {
    setUpdatingId(userId);
    try {
      const response = await workshopApi.updateCoOrganizerPermissions(
        workshopId,
        userId,
        permissions
      );

      if (response.success) {
        setCoOrganizers((prev) =>
          prev.map((co) =>
            co.user._id === userId
              ? { ...co, permissions: response.data.permissions || permissions }
              : co
          )
        );

        if (onUpdate) {
          onUpdate();
        }
        return true;
      }
      return false;
    } catch (err) {
alert(err.message || "Failed to update permissions");
      return false;
    } finally {
      setUpdatingId(null);
    }
  }, [workshopId, onUpdate]);

  const handleRemove = useCallback(async (userId, userName) => {
    setRemovingId(userId);
    try {
      const response = await workshopApi.removeCoOrganizer(workshopId, userId);

      if (response.success) {
        setCoOrganizers((prev) => prev.filter((co) => co.user._id !== userId));

        if (onUpdate) {
          onUpdate();
        }
      }
    } catch (err) {
alert(err.message || "Failed to remove co-organizer");
    } finally {
      setRemovingId(null);
    }
  }, [workshopId, onUpdate]);

  // Loading State
  if (loading) {
    return (
      <div className="com-loading">
        <FiLoader className="com-spinner com-spinner-large" />
        <span>Loading co-organizers...</span>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="com-error-state">
        <FiAlertCircle />
        <span>{error}</span>
        <button className="com-btn com-btn-ghost" onClick={fetchCoOrganizers}>
          Try Again
        </button>
      </div>
    );
  }

  return (
    <motion.div
      className="com-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <div className="com-header">
        <div className="com-header-title">
          <FiUsers />
          <h3>Co-Organizers</h3>
          <span className="com-count">
            {coOrganizers.length}/{MAX_CO_ORGANIZERS}
          </span>
        </div>

        {isOrganizer && coOrganizers.length > 0 && !showAddPanel && (
          <button
            className="com-btn com-btn-primary"
            onClick={() => setShowAddPanel(true)}
            disabled={coOrganizers.length >= MAX_CO_ORGANIZERS}
          >
            <FiPlus />
            Add
          </button>
        )}
      </div>

      {/* Add Panel */}
      <AnimatePresence>
        {isOrganizer && showAddPanel && (
          <AddCoOrganizerPanel
            workshopId={workshopId}
            onCoOrganizerAdded={handleCoOrganizerAdded}
            currentCount={coOrganizers.length}
            onClose={() => setShowAddPanel(false)}
          />
        )}
      </AnimatePresence>

      {/* Co-Organizer List */}
      {coOrganizers.length === 0 ? (
        <EmptyState isOrganizer={isOrganizer} onAddClick={() => setShowAddPanel(true)} />
      ) : (
        <motion.div className="com-list" variants={containerVariants}>
          {coOrganizers.map((coOrganizer) => (
            <CoOrganizerCard
              key={coOrganizer.user._id}
              coOrganizer={coOrganizer}
              isOrganizer={isOrganizer}
              onUpdatePermissions={handleUpdatePermissions}
              onRemove={handleRemove}
              isUpdating={updatingId === coOrganizer.user._id}
              isRemoving={removingId === coOrganizer.user._id}
            />
          ))}
        </motion.div>
      )}
    </motion.div>
  );
};

export default CoOrganizerManagement;
